/**
 * figureDownload — 편집기·그림 창의 [SVG 다운로드]·[PNG 다운로드](계약 BE-20261015 §6-2).
 *
 * 기존 `figureImage.js` 의 `toPngDataUri`(LLM 입력용 — 긴 변 900~1800px·흰 배경)는 그대로 두고, 사람이 내려받는
 * 용도는 여기서 따로 만든다: 배율(scale)과 투명 배경(기본)을 고를 수 있다. 다운로드는 a[download] + Blob URL.
 * 네트워크 호출 없음 — 입력은 전부 SVG 원문(data-URI 로 구워 <img>·canvas 에 넣는다).
 */
import { toDataUri } from './figureSvg';
import { embedFigureFont } from './figureFont';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * 래스터용 사본: viewBox 에서 크기를 읽고 루트에 width/height 를 박는다 — Firefox 는 루트 width/height 가 없는 SVG 를
 * canvas 에 그리지 않고, Chrome 은 viewBox 만 있으면 300×150 기본 크기로 읽어 naturalWidth 가 믿을 만하지 않다.
 */
function rasterSource(svgText, scale) {
  const doc = new DOMParser().parseFromString(String(svgText), 'image/svg+xml');
  const root = doc.documentElement;
  if (doc.querySelector('parsererror') || root.localName !== 'svg' || root.namespaceURI !== SVG_NS) {
    throw new Error('SVG 문법이 올바르지 않습니다.');
  }
  const vb = (root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  let w = vb.length === 4 ? vb[2] : NaN;
  let h = vb.length === 4 ? vb[3] : NaN;
  if (!(w > 0 && h > 0)) {
    w = parseFloat(root.getAttribute('width'));
    h = parseFloat(root.getAttribute('height'));
  }
  if (!(w > 0 && h > 0) || !Number.isFinite(w * h)) throw new Error('그림 크기를 읽지 못했습니다.');
  root.setAttribute('width', String(w));
  root.setAttribute('height', String(h));
  return {
    src: toDataUri(embedFigureFont(new XMLSerializer().serializeToString(root))),   // 화면과 같은 글꼴(KyMath)로 받는다 — 오너 결정 2026-10-19
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/**
 * SVG 원문 → PNG data-URI. `scale` 은 viewBox 좌표 1 당 픽셀 수(기본 2), `transparent=false` 면 흰 배경을 깐다.
 * @returns {Promise<string>} `data:image/png;base64,…`
 */
export function svgToPngDataUri(svgText, { scale = 2, transparent = true } = {}) {
  return new Promise((resolve, reject) => {
    let source;
    try {
      source = rasterSource(svgText, scale);
    } catch (e) {
      reject(e);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d');
        if (!transparent) {
          // 내려받는 PNG 의 바탕(종이색)이다 — 테마 토큰 대상이 아니다(figureImage.js:53 과 같은 이유).
          // eslint-disable-next-line no-restricted-syntax
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(new Error('그림을 PNG 로 바꾸지 못했습니다.'));
      }
    };
    img.onerror = () => reject(new Error('그림을 읽지 못했습니다.'));
    img.src = source.src;
  });
}

function dataUriToBlob(uri) {
  const comma = uri.indexOf(',');
  const type = (/^data:([^;,]+)/.exec(uri) || [])[1] || 'application/octet-stream';
  const binary = atob(uri.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** SVG 원문을 그대로 .svg 파일로. */
export function downloadSvg(svgText, filename = 'figure.svg') {
  saveBlob(new Blob([String(svgText)], { type: 'image/svg+xml;charset=utf-8' }), filename);
}

/** SVG 원문을 PNG 로 구워 .png 파일로. 완료되면 PNG data-URI 로 resolve(미리보기·테스트용). */
export async function downloadPng(svgText, filename = 'figure.png', options) {
  const dataUri = await svgToPngDataUri(svgText, options);
  saveBlob(dataUriToBlob(dataUri), filename);
  return dataUri;
}
